// Detects new web deploys at runtime and prompts the user to reload.
//
// How it works:
//   - Vite emits /version.json at build time (see vite.config.ts) and bakes
//     the same version into import.meta.env.VITE_BUILD_VERSION.
//   - <UpdateChecker /> polls /version.json every 2 minutes (only when the
//     tab is visible) plus on visibilitychange. When the served version no
//     longer matches the loaded version, a tiny zustand store flips on.
//   - <UpdatePill /> (desktop, in Header) and <UpdateToast /> (mobile, fixed
//     bottom) subscribe to the store. Both are sticky -- no dismiss -- and
//     gated on cart-empty + no open modal + no in-flight mutation, so a
//     reload can't blow up mid-sale.
//   - <UpdateAutoApply /> silently applies the update on /login once the
//     screen is idle for a short window (no typing/taps), so unattended PIN
//     screens self-heal without staff intervention.

import { useEffect, useRef } from 'react'
import { ArrowDownToLine } from 'lucide-react'
import { create } from 'zustand'
import { useIsMutating } from '@tanstack/react-query'
import { useCartStore } from '@/stores/cartStore'
import { useConnectionHealthStore } from '@/stores/connectionHealthStore'
import { useUIStore } from '@/stores/uiStore'
import { broadcastReloadNow } from '@/lib/reloadChannel'

interface UpdateState {
  available: boolean
  latestVersion: string | null
  setAvailable: (v: string) => void
}

const useUpdateStore = create<UpdateState>((set) => ({
  available: false,
  latestVersion: null,
  setAvailable: (v) => set({ available: true, latestVersion: v }),
}))

const POLL_INTERVAL_MS = 2 * 60 * 1000
const AUTO_APPLY_IDLE_MS = 15_000
const AUTO_APPLY_CHECK_MS = 1_000
// `/customer` is the customer-facing display (CFD). It's a passive
// read-only mirror — no cart, no modals, no input — so it can self-apply
// updates as soon as it detects them. This is a backstop: in the normal
// flow POS broadcasts a reload signal in `applyUpdate()` and the CFD
// reloads in lockstep. Auto-apply here covers cases where the broadcast
// was missed (CFD opened after POS update, transport dropped frame, etc.).
const AUTO_APPLY_PATHS = new Set(['/login', '/customer'])
const AUTO_APPLY_STORAGE_KEY = 'waio.pos.lastAutoReloadVersion'

async function fetchLatestVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    })
    // Piggybacked connectivity signal: this poll already runs every two
    // minutes against the CDN origin, so its outcome doubles as the
    // "internet reachable?" input for connection health (used to split
    // server-down from internet-down) at zero added traffic.
    useConnectionHealthStore.getState().recordVersionProbe(res.ok)
    if (!res.ok) return null
    const data = (await res.json()) as { version?: unknown }
    return typeof data.version === 'string' ? data.version : null
  } catch {
    // Network-level failure to reach the CDN — record for the
    // internet-reachability split.
    useConnectionHealthStore.getState().recordVersionProbe(false)
    return null
  }
}

/** Mounts the polling effect. No UI. Mount once at app root. */
export function UpdateChecker() {
  const setAvailable = useUpdateStore((s) => s.setAvailable)
  const startedRef = useRef(false)

  useEffect(() => {
    if (import.meta.env.DEV) return
    const currentVersion = import.meta.env.VITE_BUILD_VERSION
    if (!currentVersion) return
    if (startedRef.current) return
    startedRef.current = true

    let cancelled = false

    const check = async () => {
      if (document.hidden) return
      const latest = await fetchLatestVersion()
      if (cancelled || !latest) return
      if (latest !== currentVersion) setAvailable(latest)
    }

    const onVisibility = () => {
      if (!document.hidden) check()
    }

    check()
    const timer = window.setInterval(check, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [setAvailable])

  return null
}

function useSafeToUpdate(): boolean {
  const lineCount = useCartStore((s) => s.lines.length)
  const activeModal = useUIStore((s) => s.activeModal)
  const mutating = useIsMutating()
  return lineCount === 0 && activeModal === null && mutating === 0
}

function applyUpdate() {
  // Tell sibling windows (CFD) to reload too, so the customer display
  // never runs older code than POS. Skip when called FROM the CFD —
  // the CFD's own auto-apply shouldn't bounce a reload back into POS
  // while staff are using it.
  if (typeof window !== 'undefined' && window.location.pathname !== '/customer') {
    broadcastReloadNow()
  }
  // Cache-busting reload. A plain reload() can re-serve a stale index.html from
  // an intermediate cache and land on the previous build's (now-deleted) bundle
  // hashes -> black screen. A fresh query param forces the document to be
  // re-fetched from origin; main.tsx strips `_r` again on successful boot.
  try {
    const u = new URL(window.location.href)
    u.searchParams.set('_r', String(Date.now()))
    window.location.replace(u.toString())
  } catch {
    window.location.reload()
  }
}

/** Auto-applies updates on idle auth screens (currently /login only). */
export function UpdateAutoApply() {
  const available = useUpdateStore((s) => s.available)
  const latestVersion = useUpdateStore((s) => s.latestVersion)
  const safe = useSafeToUpdate()
  const lastInteractionAtRef = useRef<number>(0)

  useEffect(() => {
    const markInteraction = () => {
      lastInteractionAtRef.current = Date.now()
    }

    // Seed once on mount so the first auto-apply check waits for the full
    // idle window without calling Date.now() during render.
    markInteraction()
    window.addEventListener('pointerdown', markInteraction)
    window.addEventListener('keydown', markInteraction)
    window.addEventListener('touchstart', markInteraction)

    return () => {
      window.removeEventListener('pointerdown', markInteraction)
      window.removeEventListener('keydown', markInteraction)
      window.removeEventListener('touchstart', markInteraction)
    }
  }, [])

  useEffect(() => {
    if (import.meta.env.DEV) return
    if (!available || !latestVersion || !safe) return

    const maybeAutoApply = () => {
      const pathname = window.location.pathname
      if (!AUTO_APPLY_PATHS.has(pathname)) return
      if (Date.now() - lastInteractionAtRef.current < AUTO_APPLY_IDLE_MS) return
      if (sessionStorage.getItem(AUTO_APPLY_STORAGE_KEY) === latestVersion) return

      sessionStorage.setItem(AUTO_APPLY_STORAGE_KEY, latestVersion)
      applyUpdate()
    }

    maybeAutoApply()
    const timer = window.setInterval(maybeAutoApply, AUTO_APPLY_CHECK_MS)
    return () => window.clearInterval(timer)
  }, [available, latestVersion, safe])

  return null
}

/** Desktop pill. Render inline in the Header. */
export function UpdatePill() {
  const available = useUpdateStore((s) => s.available)
  const safe = useSafeToUpdate()
  if (!available) return null

  return (
    <button
      onClick={safe ? applyUpdate : undefined}
      disabled={!safe}
      title={
        safe
          ? 'Reload to apply the latest update'
          : 'Finish current sale before updating'
      }
      className={`hidden md:flex items-center gap-2 px-3 h-9 rounded-lg text-xs font-semibold transition-colors ${
        safe
          ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 cursor-pointer animate-update-pulse'
          : 'bg-amber-50 text-amber-600 cursor-not-allowed'
      }`}
    >
      <ArrowDownToLine size={14} />
      <span>{safe ? 'Update' : 'Update ready'}</span>
    </button>
  )
}

/** Mobile toast. Fixed bottom-center. Render once at app root. */
export function UpdateToast() {
  const available = useUpdateStore((s) => s.available)
  const safe = useSafeToUpdate()
  if (!available) return null

  return (
    <div className="md:hidden fixed bottom-4 left-4 right-4 z-50 pointer-events-none flex justify-center">
      <button
        onClick={safe ? applyUpdate : undefined}
        disabled={!safe}
        className={`pointer-events-auto flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-semibold transition-colors ${
          safe
            ? 'bg-amber-500 hover:bg-amber-600 text-white cursor-pointer animate-update-pulse-toast'
            : 'bg-amber-200 text-amber-800 cursor-not-allowed'
        }`}
      >
        <ArrowDownToLine size={16} />
        <span>{safe ? 'Tap to update' : 'Update ready — finish sale'}</span>
      </button>
    </div>
  )
}
